import { AppPagination, AppTable } from '@/components/atoms';
import { EMPTY_TEXT_FILTERED } from '@/constants';
import type { MassagerListItem } from '@/services/admin/massagers';
import { Empty } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import React from 'react';

/** 読点 「、」(U+3001) for Japanese-style separation */
const JA_SEP = '、';

/** Build 資格 label from license fields: はり師 → きゅう師 → マッサージ師 (A2 before R2). */
function getQualificationLabel(row: MassagerListItem): string {
  const parts: string[] = [];
  if (row.acupuncture_license_number?.trim()) parts.push('はり師');
  if (row.moxibustion_license_number?.trim()) parts.push('きゅう師');
  if (row.license_number?.trim()) parts.push('マッサージ師');
  return parts.join(JA_SEP);
}

type SortDir = 'ascend' | 'descend';

type SortField =
  | 'clinics.clinic_number'
  | 'clinic_informations.name'
  | 'massagers.serial_number'
  | 'massagers.name'
  | 'massagers.login_id';

type MassagersMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type MassagersTableSectionProps = {
  site: string;
  rows: MassagerListItem[];
  loading: boolean;
  meta: MassagersMeta;
  defaultPerPage: number;
  /** When true (super-admin or family admin), show サイト区分 column. */
  canUseSiteTypeFeatures?: boolean;
  tenantNameBySiteType?: Map<string, string>;
  sortState: { field: SortField; dir: SortDir } | null;
  onSort: (field: SortField | null, dir: SortDir | null) => void;
  onChangePage: (page: number) => void;
};

const MassagersTableSection: React.FC<MassagersTableSectionProps> = ({
  site: _site,
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures = false,
  tenantNameBySiteType = new Map(),
  sortState,
  onSort,
  onChangePage,
}) => {
  const SORT_FIELD_MAP: Record<string, SortField> = {
    clinic_number: 'clinics.clinic_number',
    clinic_name: 'clinic_informations.name',
    serial_number: 'massagers.serial_number',
    massager_name: 'massagers.name',
    login_id: 'massagers.login_id',
  };

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: SorterResult<MassagerListItem> | SorterResult<MassagerListItem>[]
  ) => {
    const sort = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!sort || !sort.order) {
      onSort(null, null);
      return;
    }

    const sortFieldKey = SORT_FIELD_MAP[String(sort.field ?? '')] ?? 'clinics.clinic_number';
    onSort(sortFieldKey, sort.order as SortDir);
  };

  return (
    <div className="bg-white rounded-md border">
      <AppTable
        isAdminSite
        columns={[
          {
            title: '治療院ID',
            dataIndex: 'clinic_number',
            key: 'clinic_number',
            sorter: true,
            sortOrder: sortState?.field === 'clinics.clinic_number' ? sortState.dir : undefined,
            render: (_clinicNumber: string, row: MassagerListItem) =>
              `${row.clinic_number ?? ''} - ${row.serial_number ?? ''}`,
            width: '15%',
          },
          {
            title: 'サイト区分',
            key: 'site_type',
            hidden: !canUseSiteTypeFeatures,
            width: '100px',
            render: (row: MassagerListItem) => {
              const st = row.site_type;
              if (!st) return '';
              return tenantNameBySiteType.get(st) ?? st;
            },
          },
          {
            title: '治療院名',
            dataIndex: 'clinic_name',
            key: 'clinic_name',
            sorter: true,
            width: '120px',
            sortOrder: sortState?.field === 'clinic_informations.name' ? sortState.dir : undefined,
          },
          {
            title: '施術者ログインID',
            dataIndex: 'login_id',
            key: 'login_id',
            width: '120px',
            sorter: true,
            sortOrder: sortState?.field === 'massagers.login_id' ? sortState.dir : undefined,
          },
          {
            title: '施術者名',
            dataIndex: 'massager_name',
            key: 'massager_name',
            width: '120px',
            sorter: true,
            sortOrder: sortState?.field === 'massagers.name' ? sortState.dir : undefined,
          },
          {
            title: '資格',
            key: 'qualification',
            width: '120px',
            render: (row: MassagerListItem) => getQualificationLabel(row),
          },
        ]}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />

      {meta && (
        <div className="p-4 flex justify-end">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={p => onChangePage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default MassagersTableSection;
